import { VossTimeLengthUnits } from '../../../Constants/VOSS/vossTimeLengthUnits';
import { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import { isFullyEditable } from './packageConstants';
import { isValidLength } from './planUtils';
import { trimmedOrUndefined } from './tierUtils';
import type { CreatePackageRequest, PackageDetail, PackageDraft, PackageDraftErrors, TargetGroupOption, UpdatePackageRequest } from './Packages.types';
import type { VossPackageType } from '../../../Constants/VOSS/vossPackageType';

const VOSS_DEFAULT_GRACE_PERIOD = { value: 10, unit: VossTimeLengthUnits.Year };
const VOSS_DEFAULT_FLAGS = { excludeNameLineFromInvoice: false, excludeFromUnpaidSubscriptionTermination: false, autoVerifyCustomer: true };

export const newPackageDraft = (): PackageDraft => ({
    name: '',
    type: null,
    externalId: '',
    gracePeriod: VOSS_DEFAULT_GRACE_PERIOD,
    comment: '',
    audiences: [],
    targetGroupIds: [],
    flags: VOSS_DEFAULT_FLAGS,
});

export const toPackageDraft = (vossPackage: PackageDetail): PackageDraft => ({
    name: vossPackage.name ?? '',
    type: typeof vossPackage.type === 'number' ? vossPackage.type : null,
    externalId: vossPackage.externalId ?? '',
    gracePeriod: {
        value: vossPackage.usageCreationGracePeriod?.value ?? 0,
        unit: vossPackage.usageCreationGracePeriod?.unit ?? VossTimeLengthUnits.Year,
    },
    comment: vossPackage.comment ?? '',
    audiences: vossPackage.audiences ?? [],
    targetGroupIds: (vossPackage.targetGroups ?? []).map((group) => group.id).filter((id): id is string => id !== undefined),
    flags: {
        excludeNameLineFromInvoice: vossPackage.excludeNameLineFromInvoice === true,
        excludeFromUnpaidSubscriptionTermination: vossPackage.excludeFromUnpaidSubscriptionTermination === true,
        autoVerifyCustomer: vossPackage.autoVerifyCustomer === true,
    },
});

export const packageDraftErrors = (draft: PackageDraft): PackageDraftErrors => ({
    name: draft.name.trim().length === 0,
    type: draft.type === null,
    gracePeriod: !isValidLength(draft.gracePeriod.value),
});

export const hasPackageDraftErrors = (errors: PackageDraftErrors): boolean => Object.values(errors).includes(true);

export const toCreatePackageRequest = (draft: PackageDraft & { type: VossPackageType }): CreatePackageRequest => ({
    name: draft.name.trim(),
    type: draft.type,
    externalId: trimmedOrUndefined(draft.externalId),
    comment: trimmedOrUndefined(draft.comment),
    usageCreationGracePeriod: draft.gracePeriod,
    audiences: draft.audiences,
    targetGroups: draft.targetGroupIds.map((id) => ({ id })),
    ...draft.flags,
});

export const toPackagePreview = (draft: PackageDraft, targetGroupOptions: TargetGroupOption[]): PackageDetail => ({
    name: draft.name.trim(),
    type: draft.type ?? undefined,
    status: VossVersionStatus.Draft,
    externalId: draft.externalId.trim(),
    usageCreationGracePeriod: draft.gracePeriod,
    comment: draft.comment.trim(),
    audiences: draft.audiences,
    targetGroups: targetGroupOptions.filter((group) => draft.targetGroupIds.includes(group.id)),
    ...draft.flags,
});

// VOSS rejects an empty external ID, so an emptied one is left out; an emptied comment is sent as '' so it clears the old value
export const toUpdatePackageRequest = (draft: PackageDraft, vossPackage: PackageDetail): UpdatePackageRequest => {
    const status = vossPackage.status ?? VossVersionStatus.Draft;
    const editable = { name: draft.name.trim(), externalId: trimmedOrUndefined(draft.externalId), status, targetGroups: draft.targetGroupIds.map((id) => ({ id })) };
    if (!isFullyEditable(vossPackage.status)) return editable;

    // a package with no grace period shows 0/Year; sending that would write a value the user never entered
    const hadGracePeriod = vossPackage.usageCreationGracePeriod !== null && vossPackage.usageCreationGracePeriod !== undefined;

    return {
        ...editable,
        type: draft.type ?? undefined,
        comment: draft.comment.trim(),
        usageCreationGracePeriod: hadGracePeriod || draft.gracePeriod.value !== 0 ? draft.gracePeriod : undefined,
        audiences: draft.audiences,
        ...draft.flags,
    };
};
